import LocaleIsoEnum from 'enums/LocaleIsoEnum';
import { CountryEnum, OrganisationBillingPaymentMethodEnum } from 'generated/graphql';
import { PUBLIC_ENV } from 'utils/publicEnv';

export const markets = {
    se: {
        backendDomain: PUBLIC_ENV.NEXT_PUBLIC_BACKEND_URL_SE,
        frontendUrl: PUBLIC_ENV.NEXT_PUBLIC_URL_SE,
        displayName: 'Svenska',
        market: 'se',
        googleMapsLanguageCode: 'sv',
        googleMapsCountryCode: 'SE',
        isoLocale: 'sv_SE',
        locale: 'sv-SE',
        countryCode: CountryEnum.Se,
        currencyFormatter: new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            maximumFractionDigits: 0,
        }),
        currencyRoundingMultiplier: 1,
        muiLocale: 'svSE',
        dateMuiLocale: 'svSE',
        organisationPaymentMethods: [
            OrganisationBillingPaymentMethodEnum.Bankgiro,
            OrganisationBillingPaymentMethodEnum.Plusgiro,
        ],
        defaultProductCategorySlug: 'produkter',
        coordinatesCenter: { lat: 62.2315, lng: 16.1932 },
        defaultMapZoom: 4,
    },
    no: {
        backendDomain: PUBLIC_ENV.NEXT_PUBLIC_BACKEND_URL_NO,
        frontendUrl: PUBLIC_ENV.NEXT_PUBLIC_URL_NO,
        displayName: 'Norsk',
        market: 'no',
        googleMapsLanguageCode: 'no',
        googleMapsCountryCode: 'NO',
        isoLocale: 'nb_NO',
        locale: 'nb-NO',
        countryCode: CountryEnum.No,
        // The nb-NO Format is different in Node and in the browser
        // That's why we use the swedish format with NOK currency instead
        currencyFormatter: new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'NOK',
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 0,
        }),
        currencyRoundingMultiplier: 1,
        muiLocale: 'nbNO',
        dateMuiLocale: 'nbNO',
        organisationPaymentMethods: [
            OrganisationBillingPaymentMethodEnum.BankAccount,
        ],
        defaultProductCategorySlug: 'produkter',
        coordinatesCenter: { lat: 65, lng: 18 },
        defaultMapZoom: 4,
    },
    fi: {
        backendDomain: PUBLIC_ENV.NEXT_PUBLIC_BACKEND_URL_FI,
        frontendUrl: PUBLIC_ENV.NEXT_PUBLIC_URL_FI,
        displayName: 'Suomi',
        market: 'fi',
        googleMapsLanguageCode: 'fi',
        googleMapsCountryCode: 'FI',
        isoLocale: 'fi_FI',
        locale: 'fi-FI',
        countryCode: CountryEnum.Fi,
        currencyFormatter: new Intl.NumberFormat('fi-FI', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
        }),
        currencyRoundingMultiplier: 100,
        muiLocale: 'fiFI',
        dateMuiLocale: 'fiFI',
        organisationPaymentMethods: [
            OrganisationBillingPaymentMethodEnum.BankAccount,
        ],
        defaultProductCategorySlug: 'tuotteet',
        coordinatesCenter: { lat: 65.5, lng: 26 },
        defaultMapZoom: 5,
    },
    sefi: {
        backendDomain: PUBLIC_ENV.NEXT_PUBLIC_BACKEND_URL_FI,
        frontendUrl: PUBLIC_ENV.NEXT_PUBLIC_URL_SEFI,
        displayName: 'Svenska (Finland)',
        market: 'sefi',
        googleMapsLanguageCode: 'sv',
        googleMapsCountryCode: 'FI',
        isoLocale: 'sv_FI',
        locale: 'sv-FI',
        countryCode: CountryEnum.Ax,
        currencyFormatter: new Intl.NumberFormat('sv-FI', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
        }),
        currencyRoundingMultiplier: 100,
        muiLocale: 'svSE',
        dateMuiLocale: 'svSE',
        organisationPaymentMethods: [
            OrganisationBillingPaymentMethodEnum.BankAccount,
        ],
        defaultProductCategorySlug: 'produkter',
        coordinatesCenter: { lat: 65.5, lng: 26 },
        defaultMapZoom: 5,
    },
    dk: {
        backendDomain: PUBLIC_ENV.NEXT_PUBLIC_BACKEND_URL_DK,
        frontendUrl: PUBLIC_ENV.NEXT_PUBLIC_URL_DK,
        displayName: 'Dansk',
        market: 'dk',
        googleMapsLanguageCode: 'da',
        googleMapsCountryCode: 'DK',
        isoLocale: 'da_DK',
        locale: 'da-DK',
        countryCode: CountryEnum.Dk,
        currencyFormatter: new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency: 'DKK',
            maximumFractionDigits: 0,
        }),
        currencyRoundingMultiplier: 1,
        muiLocale: 'daDK',
        dateMuiLocale: 'enUS',
        organisationPaymentMethods: [
            OrganisationBillingPaymentMethodEnum.BankAccount,
        ],
        defaultProductCategorySlug: 'produkter',
        coordinatesCenter: { lat: 56.1, lng: 10.8 },
        defaultMapZoom: 7,
    },
} as const;

export type MarketType = keyof typeof markets;

export const enum MarketCodesEnum {
    Sweden = 'se',
    Norway = 'no',
    Finland = 'fi',
    FinlandSwedish = 'sefi',
    Denmark = 'dk',
}

export const marketObjects = Object.values(markets);

export const marketLocales = Object.values(markets).map((market) => market.locale);

export const marketIsoLocales = Object.values(markets).map((market) => market.isoLocale);

export const marketDisplayNames = Object.values(markets).map((market) => market.displayName);

export const marketsList = Object.keys(markets) as MarketType[];

export const DEFAULT_MARKET: MarketType = MarketCodesEnum.Sweden;

export const languageIsoLocales = marketIsoLocales.filter((locale) => locale !== LocaleIsoEnum.SwedishFinland);
